<template>
   <div data-cy="profile-representative-name-block" style="min-height: 7rem">
      <TextBox
         id="full-name"
         v-model.trim="state.input.fullName"
         :label="$t('user-data.fullname')"
         :maxLength="300"
         :hasError="showBlockErrors && $v.input.fullName.$error"
         :errorHelper="fullNameHelperData"
         @change="processInputs"
      />
   </div>
</template>

<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength, helpers } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";

export interface ProfileRepresNameAnswerData {
   fullName: string;
}
interface Props {
   block: FlowselBlock; // we could use ProfileRepName but this would be the same as FlowselBlock
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const emit = defineEmits(["update-valid", "update-answer"]);
const userStore = useUserStore();
const { t: $t } = useI18n();

const props = defineProps<Props>();

const state = reactive({
   input: { fullName: "" },
});

// Vuelidate & rules
// -----------------------------------------
const rules = {
   input: {
      fullName: {
         $autoDirty: true,
         ...(props.block.isRequired && { required: helpers.withMessage($t("validation.field_required"), required) }),
         maxLength: helpers.withMessage($t("validation.address.invalid_length"), maxLength(150)),
      },
   },
};
const $v: any = useVuelidate(rules, state);

const fullNameHelperData = computed(() => {
   if ($v.value.input.fullName.$errors.length === 0) return null;
   return {
      text: $v.value.input.fullName.$errors[0].$message,
      code: $v.value.input.fullName.$errors[0].$validator,
   };
});

// methods
async function checkUserBlockAnswer() {
   if (props.userBlockAnswer?.data?.fullName) {
      state.input.fullName = props.userBlockAnswer.data.fullName;
   } else {
      try {
         await userStore.setUser();
         if (userStore.user?.representedByFullname) {
            state.input.fullName = userStore.user?.representedByFullname;
         } else {
            console.info("No profile data available for user");
         }
      } catch (e) {
         console.error("Failed to fetch user data", e);
      }
   }
   processInputs();
}

function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   if ($v.value.$invalid || $v.value.$error) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid
      emit("update-valid", true);
      emit(
         "update-answer",
         state.input.fullName
            ? {
                 full_name: state.input.fullName,
              }
            : null
      );
   }
}

checkUserBlockAnswer();
</script>
